import { Box, ButtonBase, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import Iconify from "src/components/iconify/iconify";
import axios, { endpoints } from "src/utils/axios";
import { downscaleImageFile } from "./downscale-image";

export type GuideSide = "front" | "rear" | "left" | "right";

export const GUIDED_PHOTO_SLOTS: { key: GuideSide; label: string }[] = [
  { key: "front", label: "Front" },
  { key: "rear", label: "Rear" },
  { key: "left", label: "Driver Side" },
  { key: "right", label: "Passenger Side" },
];

// top-view trailer outline (wheels on the sides) with the side to photograph highlighted
function TrailerGuide({ side }: { side: GuideSide }) {
  const accent = "#1877F2";
  const highlight = {
    front: { x1: 14, y1: 8, x2: 46, y2: 8 },
    rear: { x1: 14, y1: 72, x2: 46, y2: 72 },
    left: { x1: 14, y1: 8, x2: 14, y2: 72 },
    right: { x1: 46, y1: 8, x2: 46, y2: 72 },
  }[side];

  return (
    <svg width="60" height="80" viewBox="0 0 60 80">
      <rect
        x="14"
        y="8"
        width="32"
        height="64"
        rx="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeDasharray="6 4"
        opacity="0.5"
      />
      {[
        { cx: 14, cy: 44 },
        { cx: 14, cy: 58 },
        { cx: 46, cy: 44 },
        { cx: 46, cy: 58 },
      ].map((wheel) => (
        <circle
          key={`${wheel.cx}-${wheel.cy}`}
          cx={wheel.cx}
          cy={wheel.cy}
          r="6"
          fill="#FFF"
          stroke="currentColor"
          strokeWidth="2.5"
          opacity="0.6"
        />
      ))}
      <line
        x1={highlight.x1}
        y1={highlight.y1}
        x2={highlight.x2}
        y2={highlight.y2}
        stroke={accent}
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

type GuidedPhotoSlotProps = {
  side: GuideSide;
  label: string;
  photoUrl: string | null;
  onCaptured: (fileUrl: string) => void;
  onRemoved: (fileUrl: string) => void;
};

function GuidedPhotoSlot({ side, label, photoUrl, onCaptured, onRemoved }: GuidedPhotoSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [savingFile, setSavingFile] = useState(false);

  const onFileSelected = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) {
      return;
    }
    setSavingFile(true);
    downscaleImageFile(selected)
      .then((file) => axios.postForm(endpoints.rentalTrailerEvents.uploadFile, { file }))
      .then((response) => response.data)
      .then((response) => {
        if (response.fileUrl) {
          onCaptured(response.fileUrl);
        }
      })
      .catch(() => {
        enqueueSnackbar("Your photo could not be uploaded. Please try again.", { variant: "error" });
      })
      .finally(() => {
        setSavingFile(false);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
      <Box
        component="input"
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        sx={{ display: "none" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFileSelected(e.target.files)}
      />
      {photoUrl ? (
        <Box position="relative">
          <Box
            component="img"
            src={photoUrl}
            onClick={() => inputRef.current?.click()}
            sx={{ width: 84, height: 100, objectFit: "cover", borderRadius: 1, cursor: "pointer" }}
          />
          <IconButton
            size="small"
            onClick={() => onRemoved(photoUrl)}
            sx={{ position: "absolute", top: 0, right: 0, background: "rgba(0,0,0,0.4)", color: "white" }}
          >
            <Iconify icon="mingcute:close-line" width={16} />
          </IconButton>
        </Box>
      ) : (
        <ButtonBase
          onClick={() => inputRef.current?.click()}
          sx={{
            width: 84,
            height: 100,
            borderRadius: 1,
            border: "2px dashed",
            borderColor: "divider",
            color: "text.disabled",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {savingFile ? <CircularProgress size={22} /> : <TrailerGuide side={side} />}
        </ButtonBase>
      )}
      <Typography variant="caption" color={photoUrl ? "success.main" : "text.secondary"}>
        {label}
      </Typography>
    </Box>
  );
}

export type GuidedPhotoSectionProps = {
  photos: string[];
  // the persisted slot->photo mapping (event.guidedPhotos); the urls also
  // appear in photos — completeness is enforced per side, not by count
  guidedPhotos: Record<GuideSide, string>;
  onAdd: (fileUrl: string) => void;
  onRemove: (fileUrl: string) => void;
  onSlotChange: (side: GuideSide, fileUrl: string) => void;
};

export function GuidedPhotoSection({ photos, guidedPhotos, onAdd, onRemove, onSlotChange }: GuidedPhotoSectionProps) {
  const slotUrls = guidedPhotos;
  const extraInputRef = useRef<HTMLInputElement>(null);
  const [savingExtra, setSavingExtra] = useState(false);

  const slotUrlValues = Object.values(slotUrls);
  const extraPhotos = photos.filter((photo) => !slotUrlValues.includes(photo));

  const onExtraFilesSelected = (files: FileList | null) => {
    if (!files?.length) {
      return;
    }
    setSavingExtra(true);
    Promise.allSettled(
      Array.from(files).map((selected) =>
        downscaleImageFile(selected)
          .then((file) => axios.postForm(endpoints.rentalTrailerEvents.uploadFile, { file }))
          .then((response) => response.data)
          .then((response) => {
            if (response.fileUrl) {
              onAdd(response.fileUrl);
            }
          })
      )
    )
      .then((results) => {
        if (results.some((result) => result.status === "rejected")) {
          enqueueSnackbar("Some photos could not be uploaded. Please try again.", { variant: "error" });
        }
      })
      .finally(() => {
        setSavingExtra(false);
        if (extraInputRef.current) {
          extraInputRef.current.value = "";
        }
      });
  };

  return (
    <Stack gap={1}>
      <Typography variant="caption" color="text.secondary">
        Tap each outline to photograph that side of the trailer
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1.5}>
        {GUIDED_PHOTO_SLOTS.map((slot) => (
          <GuidedPhotoSlot
            key={slot.key}
            side={slot.key}
            label={slot.label}
            photoUrl={slotUrls[slot.key] || null}
            onCaptured={(fileUrl) => {
              const previous = slotUrls[slot.key];
              if (previous) {
                onRemove(previous);
              }
              onSlotChange(slot.key, fileUrl);
              onAdd(fileUrl);
            }}
            onRemoved={(fileUrl) => {
              onSlotChange(slot.key, "");
              onRemove(fileUrl);
            }}
          />
        ))}

        <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
          <Box
            component="input"
            ref={extraInputRef}
            type="file"
            accept="image/*"
            multiple
            sx={{ display: "none" }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onExtraFilesSelected(e.target.files)}
          />
          <ButtonBase
            onClick={() => extraInputRef.current?.click()}
            sx={{
              width: 84,
              height: 100,
              borderRadius: 1,
              border: "2px dashed",
              borderColor: "divider",
              color: "text.disabled",
            }}
          >
            {savingExtra ? <CircularProgress size={22} /> : <Iconify icon="mingcute:add-line" width={28} />}
          </ButtonBase>
          <Typography variant="caption" color="text.secondary">
            More Photos
          </Typography>
        </Box>
      </Box>
      {extraPhotos.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {extraPhotos.map((photo) => (
            <Box key={photo} position="relative">
              <Box component="img" src={photo} sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1 }} />
              <IconButton
                size="small"
                onClick={() => onRemove(photo)}
                sx={{ position: "absolute", top: 0, right: 0, background: "rgba(0,0,0,0.4)", color: "white" }}
              >
                <Iconify icon="mingcute:close-line" width={14} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}
